import { createContext, useContext, useMemo } from "react";

/**
 * Cloudflare Image Resizing widths used to build the responsive `srcSet`.
 *
 * Docs images never render wider than the ~768px prose column, so the top step
 * is 1536 (768px × 2 DPR). The previous 2560 step only caused retina screens to
 * download a huge variant for an image displayed at a few hundred pixels.
 */
const SRCSET_WIDTHS = [640, 960, 1280, 1536];

/**
 * Width used for the optimized `src` fallback. Browsers that support `srcSet`
 * pick from it and ignore `src`, but the fallback (old browsers, a srcSet parse
 * failure, crawlers/embed previews that read `src`) should still get a resized,
 * format-negotiated image rather than the raw original PNG.
 */
const FALLBACK_WIDTH = 1280;

/**
 * Default `sizes` for a full-bleed content image: it fills the viewport on small
 * screens and is capped at the 768px prose column on larger ones. Without this
 * the browser assumes `100vw` and picks the largest `srcSet` candidate even for
 * images that only display at a fraction of the viewport width.
 */
const DEFAULT_SIZES = "(min-width: 768px) 768px, 100vw";

/**
 * Lets a wrapper (e.g. `ModalScreenshot`) tell nested images the width they
 * actually render at, so the browser fetches a right-sized variant instead of
 * the full-width default.
 */
export const ImageSizesContext = createContext<string | undefined>(undefined);

const cdnImageUrl = (path: string, width: number) =>
  `https://cdn.zuplo.com/cdn-cgi/image/fit=contain,width=${width},format=auto${path}`;

export const Image = (
  props: React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  >,
) => {
  const contextSizes = useContext(ImageSizesContext);

  const url = useMemo(() => {
    if (!props.src || !props.src.startsWith("http")) {
      return null;
    }
    try {
      return new URL(props.src);
    } catch (e) {
      console.error("Error parsing URL", props.src);
      return null;
    }
  }, [props.src]);

  if (!props.src) {
    return <img {...props} />;
  }

  let srcSet: string | undefined;
  let optimizedSrc: string | undefined;
  if (
    process.env.VERCEL &&
    url?.hostname === "cdn.zuplo.com" &&
    !props.srcSet &&
    !props.src.endsWith(".svg") &&
    !props.src.endsWith(".gif")
  ) {
    try {
      const path = /^https?:/.test(props.src) ? url?.pathname : props.src;

      srcSet = SRCSET_WIDTHS.map(
        (width) => `${cdnImageUrl(path, width)} ${width}w`,
      ).join(", ");
      optimizedSrc = cdnImageUrl(path, FALLBACK_WIDTH);
    } catch (e) {
      console.error("Error parsing URL", props.src);
    }
  }

  const resolvedSrcSet = props.srcSet ?? srcSet;

  return (
    <img
      // Defer images by default: they sit below the fold on docs pages, so this
      // keeps them off the critical path. React 19 also skips its automatic
      // <img> preload when loading="lazy", which removes the render-blocking
      // image-preload storm from the document <head>.
      loading="lazy"
      decoding="async"
      sizes={resolvedSrcSet ? (contextSizes ?? DEFAULT_SIZES) : undefined}
      {...props}
      // Optimize the `src` fallback too, so it isn't the raw full-size PNG.
      src={optimizedSrc ?? props.src}
      srcSet={resolvedSrcSet}
    />
  );
};
